import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getPostBySlug,
  upsertPostBySlug,
  validatePost,
  type ContentPostPayload,
} from "../lib/contentAdmin";
import { OpsApiError } from "../lib/api";
import { ArticlePreview } from "../components/PreviewPane";
import { SplitPane } from "../components/SplitPane";

const EMPTY: ContentPostPayload = {
  slug: "",
  title: "",
  status: "draft",
  post_type: "post",
  body_markdown: "",
  locale: "en",
  source: "manual",
  geo_payload: { content_type: "guide" },
};

export function ContentEditorPage() {
  const { slug: slugParam } = useParams();
  const isNew = !slugParam || slugParam === "new";
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ContentPostPayload>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [issues, setIssues] = useState<Array<{ field: string; message: string }>>([]);

  const load = useCallback(async () => {
    if (isNew || !slugParam) return;
    setLoading(true);
    try {
      const post = await getPostBySlug(slugParam);
      setDraft({
        slug: String(post.slug || ""),
        title: String(post.title || ""),
        status: "publish",
        post_type: (post.post_type as ContentPostPayload["post_type"]) || "post",
        excerpt: (post.excerpt as string) || null,
        body_markdown: String(post.body_markdown || ""),
        seo_title: (post.seo_title as string) || null,
        seo_description: (post.seo_description as string) || null,
        locale: String(post.locale || "en"),
        travel_stage: (post.travel_stage as string) || null,
        city: (post.city as string) || null,
        topic_key: (post.topic_key as string) || null,
        geo_payload:
          (post.geo_payload as Record<string, unknown>) ||
          ({ content_type: "guide" } as Record<string, unknown>),
        category_slugs: Array.isArray(post.categories)
          ? (post.categories as Array<{ slug: string }>).map((c) => c.slug)
          : [],
        source: "manual",
      });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, [isNew, slugParam]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateField<K extends keyof ContentPostPayload>(key: K, value: ContentPostPayload[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setMessage(null);
    setIssues([]);
  }

  async function onValidate() {
    setBusy(true);
    setMessage(null);
    setIssues([]);
    try {
      const res = await validatePost(draft);
      if (res.ok) setMessage("Validation passed — ready to publish.");
      else {
        setIssues(res.errors || []);
        setMessage("Validation found issues.");
      }
    } catch (e) {
      const err = e as OpsApiError & { issues?: Array<{ field: string; message: string }> };
      setMessage(err.message);
      setIssues(err.issues || []);
    } finally {
      setBusy(false);
    }
  }

  async function onPublish(e: FormEvent) {
    e.preventDefault();
    const slug = draft.slug.trim().toLowerCase();
    if (!slug) {
      setMessage("Slug is required.");
      return;
    }
    setBusy(true);
    setMessage(null);
    setIssues([]);
    try {
      const res = await upsertPostBySlug(slug, { ...draft, slug, status: "publish" });
      if (res && "errors" in res && Array.isArray(res.errors)) {
        setIssues(res.errors as Array<{ field: string; message: string }>);
        setMessage("Publish blocked by validation.");
        return;
      }
      setMessage(`Published “${String(res.title || slug)}”.`);
      if (isNew) navigate(`/content/${slug}`, { replace: true });
    } catch (err) {
      const e = err as OpsApiError & { issues?: Array<{ field: string; message: string }> };
      setMessage(e.message);
      setIssues(e.issues || []);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="ops-muted">Loading post…</p>;

  return (
    <SplitPane>
    <div className="ops-panel">
      <div className="ops-toolbar">
        <Link className="ops-btn ops-btn--ghost" to="/content">
          ← Posts
        </Link>
        <span className="ops-muted">{isNew ? "New post" : `Editing ${draft.slug}`}</span>
      </div>
      <form className="ops-form" onSubmit={(ev) => void onPublish(ev)}>
        <div className="ops-form__grid">
          <label>
            <span>Slug</span>
            <input
              className="ops-input"
              disabled={!isNew}
              onChange={(e) => updateField("slug", e.target.value)}
              required
              value={draft.slug}
            />
          </label>
          <label>
            <span>Title</span>
            <input
              className="ops-input"
              onChange={(e) => updateField("title", e.target.value)}
              required
              value={draft.title}
            />
          </label>
          <label>
            <span>Content type</span>
            <input
              className="ops-input"
              onChange={(e) =>
                updateField("geo_payload", {
                  ...(draft.geo_payload ?? {}),
                  content_type: e.target.value,
                })
              }
              value={String((draft.geo_payload as { content_type?: string })?.content_type ?? "")}
            />
          </label>
          <label>
            <span>Topic key</span>
            <input
              className="ops-input"
              onChange={(e) => updateField("topic_key", e.target.value || null)}
              value={draft.topic_key ?? ""}
            />
          </label>
          <label>
            <span>City</span>
            <input
              className="ops-input"
              onChange={(e) => updateField("city", e.target.value || null)}
              value={draft.city ?? ""}
            />
          </label>
          <label>
            <span>SEO description</span>
            <input
              className="ops-input"
              onChange={(e) => updateField("seo_description", e.target.value || null)}
              value={draft.seo_description ?? ""}
            />
          </label>
        </div>
        <label className="ops-form__full">
          <span>Body (Markdown)</span>
          <textarea
            className="ops-input ops-textarea"
            onChange={(e) => updateField("body_markdown", e.target.value)}
            rows={18}
            value={draft.body_markdown ?? ""}
          />
        </label>
        {message ? <p className={message.includes("failed") || message.includes("blocked") ? "ops-error" : "ops-ok"}>{message}</p> : null}
        {issues.length > 0 ? (
          <ul className="ops-issues">
            {issues.map((issue) => (
              <li key={`${issue.field}-${issue.message}`}>
                <strong>{issue.field}</strong>: {issue.message}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="ops-toolbar">
          <button className="ops-btn ops-btn--ghost" disabled={busy} onClick={() => void onValidate()} type="button">
            Validate
          </button>
          <button className="ops-btn ops-btn--primary" disabled={busy} type="submit">
            {busy ? "Saving…" : "Publish"}
          </button>
        </div>
      </form>
    </div>
    <ArticlePreview
      item={{
        title: draft.title,
        excerpt: draft.excerpt || draft.seo_description || "",
        body: draft.body_markdown,
        kind: String((draft.geo_payload as { content_type?: string })?.content_type || draft.post_type || "guide"),
        city: draft.city || "",
      }}
    />
    </SplitPane>
  );
}
